import React, { createContext, useContext, useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type BusinessHours = {
  dias: string;
  horario: string;
};

type Tenant = {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  primary_color: string | null;
  address: string | null;
  city: string | null;
  description: string | null;
  business_hours: BusinessHours[] | null;
  public_phone: string | null;
  primary_category: string | null;
};

type TenantContextType = {
  tenant: Tenant | null;
  isLoading: boolean;
};

const TenantContext = createContext<TenantContextType>({
  tenant: null,
  isLoading: true,
});

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  
  const searchParams = new URLSearchParams(location.search);
  const querySlug = searchParams.get("s");
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const firstSegment = pathSegments[0];
  const reservedRoutes = new Set(["admin", "super-admin", "pedido", "sobre", "reset-password", "garcom"]);
  const pathSlug = firstSegment && !reservedRoutes.has(firstSegment) ? firstSegment : null;
  
  // Se estivermos em uma rota de admin global, não queremos carregar um tenant específico
  const isAdminRoute =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/super-admin") ||
    location.pathname.startsWith("/garcom");
  // A landing comercial (raiz e /planos) não pertence a nenhuma loja
  const isLandingRoute = location.pathname === "/" || location.pathname === "/planos";
  const slug = isAdminRoute || isLandingRoute ? null : (pathSlug || querySlug || "tiapreta");

  const { data: tenant, isLoading } = useQuery({
    queryKey: ["tenant", slug],
    queryFn: async () => {
      if (!slug) return null;
      
      const { data, error } = await supabase
        .rpc("get_tenant_public_by_slug", { _slug: slug });

      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return (row ?? null) as unknown as Tenant | null;
    },
    staleTime: 1000 * 60 * 10,
  });

  useEffect(() => {
    if (isAdminRoute) {
      document.documentElement.style.setProperty("--primary", "222 47% 11%"); // Slate-950 ou cor padrão admin
      document.documentElement.style.setProperty("--ring", "222 47% 11%");
      document.title = "Plataforma Delivery - Super Admin";
      return;
    }

    if (tenant?.primary_color) {
      // Update CSS variables dynamically
      const color = tenant.primary_color;
      // We expect a hex color. We need to convert it to HSL for Shadcn/Tailwind if possible,
      // but for simplicity, we can just set a new CSS variable and use it in Tailwind.
      document.documentElement.style.setProperty("--primary", hexToHSL(color));
      document.documentElement.style.setProperty("--ring", hexToHSL(color));
    } else {
      // Fallback to default Tia Preta color (Orange-ish) if no color is set
      document.documentElement.style.setProperty("--primary", "15 80% 50%");
      document.documentElement.style.setProperty("--ring", "15 80% 50%");
    }

    if (tenant?.name) {
      document.title = tenant.name;
    }
  }, [tenant, isAdminRoute]);

  return (
    <TenantContext.Provider value={{ tenant, isLoading }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => useContext(TenantContext);

// Helper to convert HEX to HSL format used by Shadcn (e.g., "15 80% 50%")
function hexToHSL(hex: string): string {
  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.substring(1, 3), 16);
    g = parseInt(hex.substring(3, 5), 16);
    b = parseInt(hex.substring(5, 7), 16);
  }
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
